<template>
  <div>
    <dialog-confirm ref="delete-confirm">
      <div class="text-subtitle-1">
        {{ $t('are_you_sure_you_want_to_delete_this') }}
      </div>
    </dialog-confirm>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold">{{ $t('recent_reports') }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div></div>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table reports-index-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template v-slot:item.customers_in_scope="row">
          <if-set :value="getCustomerName(row.item)" dash>
            <template v-slot:default="scope">
              {{ scope.value }}
            </template>
          </if-set>
        </template>
        <template v-slot:item.name="row">
          <router-link :to="`/report/${row.item.id}`">
            <v-icon color="primary" v-text="row.item.mdiIcon"/>
            {{ $t(row.value) }}
          </router-link>
        </template>
        <template v-slot:item.results_generated="row">
          <if-set :value="row.value" dash>
            <multi-time-display :time="row.value" />
          </if-set>
        </template>
        <template v-slot:item.status="row">
          {{ $t(row.value) }}
        </template>
        <template v-slot:item.actions="row">
          <div class="ml-n2 text-no-wrap">
            <vf-tooltip :text="$t('delete')">
              <v-btn @click="deleteReport(row.item)" color="primary" fab text small>
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </vf-tooltip>
            <vf-tooltip :text="$t('use_as_template')">
              <v-btn @click="cloneReport(row.item)" color="primary" fab text small>
                <v-icon>mdi-content-copy</v-icon>
              </v-btn>
            </vf-tooltip>
            <vf-tooltip :text="$t('view')">
              <v-btn :to="`/report/${row.item.id}`" color="primary" fab text small>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </vf-tooltip>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import DialogConfirm from '@/components/shared/DialogConfirm'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import VfTooltip from '@/components/shared/VfTooltip'
import IfSet from '@/components/shared/IfSet'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import { size, first, get } from 'lodash'

export default {
  name: 'ReportIndex',
  components: {
    DialogConfirm,
    TablePaginationControls,
    MultiTimeDisplay,
    VfTooltip,
    IfSet
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin
  ],
  data () {
    return {
      model: 'Report',
      remoteData: {
        rowsPerPage: 10
      },
      tableFields: [
        {
          value: 'name',
          text: this.$tc('report'),
          sortable: false
        },
        {
          value: 'customers_in_scope',
          text: this.$tc('dc_location_type'),
          sortable: false
        },
        {
          value: 'results_generated',
          text: this.$tc('generated_on'),
          sortable: true
        },
        {
          value: 'status',
          text: this.$tc('status'),
          sortable: true
        },
        {
          value: 'actions',
          text: this.$tc('action', 2),
          sortable: false
        }
      ]
    }
  },
  methods: {
    /**
     * @param {ReportRecord} report
     * @returns {VueI18n.TranslateResult|*}
     */
    getCustomerName (report) {
      if (size(report.customers_in_scope) > 1) {
        return this.$t('multiple')
      } else {
        return get(first(report.customers_in_scope), 'name')
      }
    },

    /**
     * Implement RemoteDataTableMixin buildSearchCriteria
     * @return Object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        requester_id: this.user.id,
        expand: 'customers_in_scope',
        fields: '*,customers_in_scope.name',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.remoteData.sortBy != null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      return searchCriteria
    },

    /**
     * Emit an event letting the dashboard know that we want to clone a report.
     * @param report
     */
    cloneReport (report) {
      this.$emit('cloneReport', report)
    },

    /**
     * Upon confirmation (via modal), deletes the report identified and refreshes the table.
     * @return undefined
     */
    async deleteReport (report) {
      if (await this.$refs['delete-confirm'].confirm()) {
        this.$gtag.event('delete', { event_category: 'Report' })
        try {
          await report.destroy()
          await this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('report')} ${this.$i18n.t('generic_delete_success')}`
          )
          this.initiateSearch()
        } catch (error) {
          this.handleError(error)
        }
      }
    }

  },
  mounted () {
    this.updateBrowserTitle(this.$tc('report', 2))
    this.initiateSearch()
  }
}
</script>
